// React hooks
import { useEffect, useRef, useState } from "react";

// PrimeReact components
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";

// Toolkit imports
import { getLeaderboard } from "sj-ui-toolkit/src/api";
import { NavUserImage } from "sj-ui-toolkit/src/atlas/components/UserNavBar/NavUserImage/NavUserImage";
import { C, themeClasses as T } from "sj-ui-toolkit/src/atlas/utils/themeClasses";

/**
 * LeaderboardPage Component
 *
 * Displays a paginated, sortable, and searchable leaderboard table with:
 * - Ranking
 * - Name
 * - Total Points
 * - Weekly Streak
 * - Current Belt (Level)
 * - Points earned in past 30 days
 *
 * Features:
 * - Server-side pagination
 * - Server-side sorting
 * - Search/filter by name
 */
const LeaderboardPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState("totalPoints");
  const [sortOrder, setSortOrder] = useState("desc");

  const searchTimeout = useRef(null);

  const loadLeaderboard = async (page, rows, query, sortBy, direction) => {
    setLoading(true);
    try {
      const { users: fetchedUsers, pagesNumber } = await getLeaderboard({
        page,
        recordsPerPage: rows,
        query: query || "",
        sortBy: sortBy || "",
        direction: direction,
      });

      setUsers(fetchedUsers);
      setTotalRecords(pagesNumber * rows);
    } catch (error) {
      console.error("Error loading leaderboard:", error);
      setUsers([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLeaderboard(currentPage, rowsPerPage, searchQuery, sortField, sortOrder);
  }, [currentPage, rowsPerPage, searchQuery, sortField, sortOrder]);

  const onPageChange = event => {
    const newPage = Math.floor(event.first / event.rows) + 1;
    setCurrentPage(newPage);
    setRowsPerPage(event.rows);
  };

  const onSort = event => {
    // If clicking on a new column (different from current sortField), default to desc
    // If clicking on the same column, toggle between desc and asc
    if (event.sortField !== sortField) {
      setSortField(event.sortField);
      setSortOrder("desc");
    } else {
      setSortField(event.sortField);
      setSortOrder(event.sortOrder === 1 ? "asc" : "desc");
    }
  };

  const onSearchChange = e => {
    const value = e.target.value;

    // Clear existing timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // Set new timeout for debounced search
    searchTimeout.current = setTimeout(() => {
      setSearchQuery(value);
      setCurrentPage(1); // Reset to first page on new search
    }, 500);
  };

  const rankingBodyTemplate = rowData => {
    return <span className="font-semibold">{rowData.ranking}</span>;
  };

  const nameBodyTemplate = rowData => {
    const name = rowData.name || rowData.username || "N/A";

    return (
      <div className="flex items-center gap-3">
        <NavUserImage customSize="32px" name={name} thumbImageUrl={rowData.thumbImageUrl} />
        <span>{name}</span>
      </div>
    );
  };

  const pointsBodyTemplate = rowData => {
    return <span>{rowData.totalPoints?.toLocaleString() || 0}</span>;
  };

  const streakBodyTemplate = rowData => {
    return <span>{rowData.weeklyStreak || 0}</span>;
  };

  const recentPointsBodyTemplate = rowData => {
    return <span>{rowData.pointsPast30Days?.toLocaleString() || 0}</span>;
  };

  return (
    <div className="h-full" aria-label="Leaderboard">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Leaderboard</h1>
        <p className={T.textMuted}>See how you rank against other learners in the community</p>
      </div>

      <div className="mb-4">
        <span className="p-input-icon-left w-full md:w-80">
          <i className="pi pi-search ml-2" />
          <InputText
            placeholder="Search by name..."
            onChange={onSearchChange}
            className="w-full pl-8"
          />
        </span>
      </div>

      <DataTable
        className={C(T.bgElevation1, "w-full")}
        value={users}
        dataKey="ranking"
        loading={loading}
        paginator
        rows={rowsPerPage}
        first={(currentPage - 1) * rowsPerPage}
        totalRecords={totalRecords}
        onPage={onPageChange}
        onSort={onSort}
        sortField={sortField}
        sortOrder={sortOrder === "asc" ? 1 : -1}
        rowsPerPageOptions={[5, 10, 25, 50]}
        lazy
        emptyMessage="No users found"
      >
        <Column header="Ranking" body={rankingBodyTemplate} style={{ width: "10%" }} />
        <Column field="name" header="Name" body={nameBodyTemplate} style={{ width: "20%" }} />
        <Column
          field="totalPoints"
          header="Total Points"
          body={pointsBodyTemplate}
          sortable
          style={{ width: "15%" }}
        />
        <Column
          field="weeklyStreak"
          header="Weekly Streak"
          body={streakBodyTemplate}
          sortable
          style={{ width: "15%" }}
        />
        <Column
          field="pointsPast30Days"
          header="Points (Past 30 Days)"
          body={recentPointsBodyTemplate}
          sortable
          style={{ width: "20%" }}
        />
      </DataTable>
    </div>
  );
};

export default LeaderboardPage;
